import ThemeConfig, { MinimalThemeInformation } from "../../Models/ThemeConfig/ThemeConfig"
import { Store, Stores } from "../UIEventSource"
import UserRelatedState from "../State/UserRelatedState"
import themeOverview from "../../assets/generated/theme_overview.json"
import { OsmConnection } from "../Osm/OsmConnection"
import { AndroidPolyfill } from "../Web/AndroidPolyfill"
import Fuse, { IFuseOptions } from "fuse.js"
import Constants from "../../Models/Constants"
import Locale from "../../UI/i18n/Locale"
import { Utils } from "../../Utils"
import { Lists } from "../../Utils/Lists"

export class ThemeSearchIndex {
    private readonly themeIndex: Fuse<MinimalThemeInformation>
    private readonly layerIndex: Fuse<{ id: string; description }>
    /**
     * A 'search' will also search matching layers from the official themes.
     * This might cause themes to show up which weren't passed in 'themesToSearch', so we create a whitelist
     */
    private readonly themeWhitelist: Set<string>

    constructor(
        language: string,
        themesToSearch: MinimalThemeInformation[],
        layersToIgnore: string[] = []
    ) {
        const themes = Utils.noNull(themesToSearch)
        if (!themes) {
            throw "No themes loaded. Did generate:layeroverview fail?"
        }
        this.themeWhitelist = new Set(themes.map((th) => th.id))
        const fuseOptions: IFuseOptions<MinimalThemeInformation> = {
            ignoreLocation: true,
            threshold: 0.2,
            shouldSort: true,
            keys: [
                { name: "id", weight: 2 },
                "title." + language,
                "keywords." + language,
                "shortDescription." + language,
            ],
        }

        const themesIndex = themes.filter((th) => th?.id !== "personal")
        console.log("Building theme index")
        this.themeIndex = new Fuse(themesIndex, fuseOptions)

        const toIgnore = new Set(layersToIgnore)
        const layersAsList: { id: string; description: Record<string, string[]> }[] = []
        for (const id in ThemeSearch.officialThemes.layers) {
            if (Constants.isPriviliged(id)) {
                continue
            }
            if (toIgnore.has(id)) {
                continue
            }
            const l: Record<string, string[]> = ThemeSearch.officialThemes.layers[id]
            layersAsList.push({ id, description: l })
        }
        this.layerIndex = new Fuse(layersAsList, {
            includeScore: true,
            minMatchCharLength: 3,
            ignoreLocation: true,
            threshold: 0.02,
            keys: ["id", "description." + language],
        })
    }

    public search(text: string, limit?: number): MinimalThemeInformation[] {
        const scored = this.searchWithScores(text)
        let result = Array.from(scored.entries()) // Already sorted, we set this option
        if (limit) {
            result = result.slice(0, limit)
        }
        return result.map((e) => ThemeSearch.officialThemesById.get(e[0]))
    }

    public searchWithScores(text: string): Map<string, number> {
        const result = new Map<string, number>()
        const themeResults = this.themeIndex.search(text)
        for (const themeResult of themeResults) {
            result.set(themeResult.item.id, themeResult.score)
        }

        const layerResults = this.layerIndex.search(text)

        for (const layer of layerResults) {
            const matchingThemes = ThemeSearch.layersToThemes.get(layer.item.id)
            const score = layer.score
            matchingThemes?.forEach((th) => {
                if (!this.themeWhitelist.has(th.id)) {
                    // This theme was not in the 'themesToSearch'
                    return
                }
                const previous = result.get(th.id) ?? 10000
                result.set(th.id, Math.min(previous, score * 5))
            })
        }

        return result
    }

    /**
     * Builds a search index containing all public and visited themes, but ignoring the layers loaded by the current theme
     */
    public static fromState(state: {
        osmConnection: OsmConnection
        theme: ThemeConfig
    }): Store<ThemeSearchIndex> {
        const layersToIgnore = state.theme.layers.filter((l) => l.isNormal()).map((l) => l.id)
        const knownHidden: Store<string[]> = Stores.listStabilized(
            UserRelatedState.initDiscoveredHiddenThemes(state.osmConnection)
                .stabilized(1000)
                .map((list) => Lists.dedup(list))
        )
        const otherThemes: MinimalThemeInformation[] = ThemeSearch.officialThemes.themes.filter(
            (th) => th.id !== state.theme.id
        )
        return Locale.language.map(
            (language) => {
                const themes = otherThemes.concat(
                    ...knownHidden.data.map((id) => ThemeSearch.officialThemesById.get(id))
                )
                return new ThemeSearchIndex(language, themes, layersToIgnore)
            },
            [knownHidden]
        )
    }
}

export default class ThemeSearch {
    public static readonly officialThemes: {
        themes: MinimalThemeInformation[]
        layers: Record<string, Record<string, string[]>>
    } = <any>themeOverview
    public static readonly officialThemesById: Map<string, MinimalThemeInformation> = new Map<
        string,
        MinimalThemeInformation
    >()

    /*
     * For every layer id, states which themes use the layer
     */
    public static readonly layersToThemes: Map<string, MinimalThemeInformation[]> = new Map()
    static {
        for (const th of ThemeSearch.officialThemes.themes ?? []) {
            ThemeSearch.officialThemesById.set(th.id, th)
            for (const layer of th.layers) {
                let list = ThemeSearch.layersToThemes.get(layer)
                if (!list) {
                    list = []
                    ThemeSearch.layersToThemes.set(layer, list)
                }
                list.push(th)
            }
        }
    }

    public static createUrlFor(layout: { id: string }, state?: { layoutToUse?: { id } }): string {
        if (layout === undefined) {
            return undefined
        }
        if (layout.id === undefined) {
            console.error("ID is undefined for layout", layout)
            return undefined
        }

        if (layout.id === state?.layoutToUse?.id) {
            return undefined
        }

        let path = window.location.pathname
        // Path starts with a '/' and contains everything, e.g. '/dir/dir/page.html'
        path = path.substr(0, path.lastIndexOf("/"))
        // Path will now contain '/dir/dir', or empty string in case of nothing
        if (path === "") {
            path = "."
        }

        let linkPrefix = `${path}/${layout.id.toLowerCase()}.html?`
        if (
            (location.hostname === "localhost" && !AndroidPolyfill.inAndroid.data) ||
            location.hostname === "127.0.0.1"
        ) {
            linkPrefix = `${path}/theme.html?layout=${layout.id}&`
        }

        if (layout.id.startsWith("http://") || layout.id.startsWith("https://")) {
            linkPrefix = `${path}/theme.html?userlayout=${layout.id}&`
        }

        return `${linkPrefix}`
    }
}
